import { action, ActionType } from 'typesafe-actions';
import update from 'immutability-helper';
import { Signature, SelfieSignature } from '../../utils/classes';

// ─── Action types ──────────────────────────────────────────────────────────

export const SET_IS_ANIMATING = '@uploadreport/SET_IS_ANIMATING';
export const SET_IS_UPLOADING = '@uploadreport/SET_IS_UPLOADING';
export const UPDATE_UPLOAD_STATUS = '@uploadreport/UPDATE_UPLOAD_STATUS';
export const SET_CANCEL_STATUS = '@uploadreport/SET_CANCEL_STATUS';

export const UPDATE_UPLOADED_PHOTOS = '@uploadreport/UPDATE_UPLOADED_PHOTOS';
export const UPDATE_UPLOADED_VIDEOS = '@uploadreport/UPDATE_UPLOADED_VIDEOS';
export const UPDATE_UPLOADED_SIGNATURES = '@uploadreport/UPDATE_UPLOADED_SIGNATURES';
export const UPDATE_UPLOADED_SELFIE_SIGNATURES = '@uploadreport/UPDATE_UPLOADED_SELFIE_SIGNATURES';

export const SET_UPLOADED_PHOTOS = '@uploadreport/SET_UPLOADED_PHOTOS';
export const SET_UPLOADED_VIDEOS = '@uploadreport/SET_UPLOADED_VIDEOS';
export const SET_UPLOADED_SIGNATURES = '@uploadreport/SET_UPLOADED_SIGNATURES';
export const SET_UPLOADED_SELFIE_SIGNATURES = '@uploadreport/SET_UPLOADED_SELFIE_SIGNATURES';
export const SET_PHOTOS_URI = '@uploadreport/SET_PHOTOS_URI';
export const SET_VIDEOS_URI = '@uploadreport/SET_VIDEOS_URI';
export const SET_SIGNATURE_PHOTOS_URI = '@uploadreport/SET_SIGNATURE_PHOTOS_URI';
export const SET_SELFIE_SIGNATURE_PHOTOS_URI = '@uploadreport/SET_SELFIE_SIGNATURE_PHOTOS_URI';

export const RESET_UPLOADED_PHOTOS = '@uploadreport/RESET_UPLOADED_PHOTOS';
export const RESET_UPLOADED_VIDEOS = '@uploadreport/RESET_UPLOADED_VIDEOS';
export const RESET_UPLOADED_SIGNATURES = '@uploadreport/RESET_UPLOADED_SIGNATURES';
export const RESET_STATE = '@uploadreport/RESET_STATE';

export const UPDATE_UPLOADED_PHOTO_COUNT = '@uploadreport/UPDATE_UPLOADED_PHOTO_COUNT';
export const UPDATE_UPLOADED_VIDEO_COUNT = '@uploadreport/UPDATE_UPLOADED_VIDEO_COUNT';
export const UPDATE_TOTAL_PHOTO_COUNT = '@uploadreport/UPDATE_TOTAL_PHOTO_COUNT';
export const UPDATE_TOTAL_VIDEO_COUNT = '@uploadreport/UPDATE_TOTAL_VIDEO_COUNT';

// ─── Action creators ────────────────────────────────────────────────────────

export const setIsAnimating = (bool: boolean) => action(SET_IS_ANIMATING, { bool });
export const setIsUploading = (bool: boolean) => action(SET_IS_UPLOADING, { bool });
export const updateUploadStatus = (status: UploadStatusKeys) => action(UPDATE_UPLOAD_STATUS, { status });
export const setCancelStatus = (bool: boolean) => action(SET_CANCEL_STATUS, { bool });

export const updateUploadedPhotos = (questionIndex: number, photoIndex: number, fileRef: string) =>
  action(UPDATE_UPLOADED_PHOTOS, { questionIndex, photoIndex, fileRef });
export const updateUploadedSignatures = (index: number, fileRef: string) =>
  action(UPDATE_UPLOADED_SIGNATURES, { index, fileRef });
export const updateUploadedSelfieSignatures = (index: number, fileRef: string) =>
  action(UPDATE_UPLOADED_SELFIE_SIGNATURES, { index, fileRef });
export const updateUploadedVideos = (questionIndex: number, videoIndex: number, fileRef: string) =>
  action(UPDATE_UPLOADED_VIDEOS, { questionIndex, videoIndex, fileRef });

export const setUploadedPhotos = (questionnairePhotos: string[][]) =>
  action(SET_UPLOADED_PHOTOS, { questionnairePhotos });
export const setUploadedVideos = (questionnaireVideos: string[][]) =>
  action(SET_UPLOADED_VIDEOS, { questionnaireVideos });
export const setUploadedSignatures = (signatures: Signature[]) => action(SET_UPLOADED_SIGNATURES, { signatures });
export const setUploadedSelfieSignatures = (selfieSignatures: SelfieSignature[]) =>
  action(SET_UPLOADED_SELFIE_SIGNATURES, { selfieSignatures });

export const setSignaturePhotosURI = (signaturePhotosURI: string[]) =>
  action(SET_SIGNATURE_PHOTOS_URI, { signaturePhotosURI });
export const setSelfieSignaturesPhotosURI = (selfieSignaturePhotosURI: string[]) =>
  action(SET_SELFIE_SIGNATURE_PHOTOS_URI, { selfieSignaturePhotosURI });
export const setPhotosURI = (photosURI: string[][]) => action(SET_PHOTOS_URI, { photosURI });
export const setVideosURI = (videosURI: string[][]) => action(SET_VIDEOS_URI, { videosURI });

export const updateUploadedPhotoCount = (count: number) => action(UPDATE_UPLOADED_PHOTO_COUNT, { count });
export const updateUploadedVideoCount = (count: number) => action(UPDATE_UPLOADED_VIDEO_COUNT, { count });
export const updateTotalPhotoCount = (sum: number) => action(UPDATE_TOTAL_PHOTO_COUNT, { sum });
export const updateTotalVideoCount = (sum: number) => action(UPDATE_TOTAL_VIDEO_COUNT, { sum });

export const resetUploadedPhotos = () => action(RESET_UPLOADED_PHOTOS);
export const resetUploadedVideos = () => action(RESET_UPLOADED_VIDEOS);
export const resetUploadedSignatures = () => action(RESET_UPLOADED_SIGNATURES);
export const resetUploadState = () => action(RESET_STATE);

// ─── Types ──────────────────────────────────────────────────────────────────

export type UploadStatusKeys = 'complete' | 'uploading' | 'error' | 'inactive';

export type UploadReportState = {
  readonly status: UploadStatusKeys;
  readonly isAnimating: boolean;
  readonly isUploading: boolean;
  readonly isCancelled: boolean;
  readonly uploadedPhotos: string[][];
  readonly uploadedSignatures: Signature[];
  readonly uploadedSelfieSignatures: SelfieSignature[];
  readonly uploadedVideos: string[][];
  readonly uploadedPhotoCount: number;
  readonly uploadedVideoCount: number;
  readonly totalPhotosCount: number;
  readonly totalVideosCount: number;
  readonly photosURI: string[][];
  readonly videosURI: string[][];
  readonly signaturePhotosURI: string[];
  readonly selfieSignaturePhotosURI: string[];
};

const initialState: UploadReportState = {
  status: 'inactive',
  isAnimating: false,
  isUploading: false,
  isCancelled: false,
  uploadedPhotos: [],
  uploadedSignatures: [],
  uploadedSelfieSignatures: [],
  uploadedVideos: [],
  uploadedPhotoCount: 0,
  uploadedVideoCount: 0,
  totalPhotosCount: 0,
  totalVideosCount: 0,
  photosURI: [],
  videosURI: [],
  signaturePhotosURI: [],
  selfieSignaturePhotosURI: []
};

export const uploadreportActions = {
  setCancelStatus,
  setIsAnimating,
  setIsUploading,
  setPhotosURI,
  setVideosURI,
  setSignaturePhotosURI,
  setSelfieSignaturesPhotosURI,
  setUploadedPhotos,
  setUploadedVideos,
  setUploadedSignatures,
  setUploadedSelfieSignatures,
  updateUploadStatus,
  updateUploadedPhotos,
  updateUploadedSignatures,
  updateUploadedSelfieSignatures,
  updateUploadedVideos,
  updateUploadedPhotoCount,
  updateUploadedVideoCount,
  updateTotalPhotoCount,
  updateTotalVideoCount,
  resetUploadedPhotos,
  resetUploadedVideos,
  resetUploadedSignatures,
  resetUploadState
};

export type UploadReportAction = ActionType<typeof uploadreportActions>;

// ─── Reducer ────────────────────────────────────────────────────────────────

export function uploadreportReducer(
  state: UploadReportState = initialState,
  incomingAction: UploadReportAction
): UploadReportState {
  switch (incomingAction.type) {
    case UPDATE_UPLOAD_STATUS:
      return { ...state, status: incomingAction.payload.status };
    case SET_PHOTOS_URI:
      return { ...state, photosURI: incomingAction.payload.photosURI };
    case SET_VIDEOS_URI:
      return { ...state, videosURI: incomingAction.payload.videosURI };
    case SET_SIGNATURE_PHOTOS_URI:
      return { ...state, signaturePhotosURI: incomingAction.payload.signaturePhotosURI };
    case SET_SELFIE_SIGNATURE_PHOTOS_URI:
      return { ...state, selfieSignaturePhotosURI: incomingAction.payload.selfieSignaturePhotosURI };
    case SET_IS_ANIMATING:
      return { ...state, isAnimating: incomingAction.payload.bool };
    case SET_IS_UPLOADING:
      return { ...state, isUploading: incomingAction.payload.bool };
    case SET_CANCEL_STATUS:
      return { ...state, isCancelled: incomingAction.payload.bool };
    case UPDATE_UPLOADED_PHOTOS:
      return update(state, {
        uploadedPhotos: {
          [incomingAction.payload.questionIndex]: {
            [incomingAction.payload.photoIndex]: { $set: incomingAction.payload.fileRef }
          }
        }
      });
    case UPDATE_UPLOADED_VIDEOS:
      return update(state, {
        uploadedVideos: {
          [incomingAction.payload.questionIndex]: {
            [incomingAction.payload.videoIndex]: { $set: incomingAction.payload.fileRef }
          }
        }
      });
    case SET_UPLOADED_VIDEOS:
      return update(state, { uploadedVideos: { $set: incomingAction.payload.questionnaireVideos } });
    case SET_UPLOADED_PHOTOS:
      return update(state, { uploadedPhotos: { $set: incomingAction.payload.questionnairePhotos } });
    case SET_UPLOADED_SIGNATURES:
      if (incomingAction.payload.signatures) {
        return { ...state, uploadedSignatures: incomingAction.payload.signatures };
      }
      return state;
    case SET_UPLOADED_SELFIE_SIGNATURES:
      return { ...state, uploadedSelfieSignatures: incomingAction.payload.selfieSignatures };
    case UPDATE_UPLOADED_SELFIE_SIGNATURES:
      return update(state, {
        uploadedSelfieSignatures: {
          [incomingAction.payload.index]: { path: { $set: incomingAction.payload.fileRef } }
        }
      });
    case UPDATE_UPLOADED_SIGNATURES:
      return update(state, {
        uploadedSignatures: {
          [incomingAction.payload.index]: { path: { $set: incomingAction.payload.fileRef } }
        }
      });
    case UPDATE_UPLOADED_PHOTO_COUNT:
      return update(state, {
        uploadedPhotoCount: { $set: Number(state.uploadedPhotoCount) + Number(incomingAction.payload.count) }
      });
    case UPDATE_UPLOADED_VIDEO_COUNT:
      return update(state, {
        uploadedVideoCount: { $set: Number(state.uploadedVideoCount) + Number(incomingAction.payload.count) }
      });
    case UPDATE_TOTAL_PHOTO_COUNT:
      return update(state, { totalPhotosCount: { $set: incomingAction.payload.sum } });
    case UPDATE_TOTAL_VIDEO_COUNT:
      return update(state, { totalVideosCount: { $set: incomingAction.payload.sum } });
    case RESET_UPLOADED_PHOTOS:
      return update(state, { uploadedPhotos: { $set: [] } });
    case RESET_UPLOADED_VIDEOS:
      return update(state, { uploadedVideos: { $set: [] } });
    case RESET_UPLOADED_SIGNATURES:
      return update(state, { uploadedSignatures: { $set: [] } });
    case RESET_STATE:
      return { ...initialState };
    default:
      return state;
  }
}
